
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Search, Filter } from 'lucide-react'
import type { FilterStatus, FilterPlan } from '../types'

// Valid filter values for runtime validation
const VALID_STATUSES: readonly FilterStatus[] = ['all', 'active', 'expired', 'revoked'] as const
const VALID_PLANS: readonly (FilterPlan | 'all')[] = ['all', 'starter', 'professional', 'enterprise'] as const

interface LicensesFiltersProps {
  searchQuery: string
  setSearchQuery: (query: string) => void
  filterStatus: FilterStatus
  setFilterStatus: (status: FilterStatus) => void
  filterPlan: FilterPlan
  setFilterPlan: (plan: FilterPlan) => void
}

function isValidStatus(value: string): value is FilterStatus {
  return VALID_STATUSES.includes(value as FilterStatus)
}

function isValidPlan(value: string): value is FilterPlan {
  return VALID_PLANS.includes(value as FilterPlan | 'all')
}

export function LicensesFilters({
  searchQuery,
  setSearchQuery,
  filterStatus,
  setFilterStatus,
  filterPlan,
  setFilterPlan,
}: LicensesFiltersProps) {
  const handleStatusChange = (value: string) => {
    if (isValidStatus(value)) {
      setFilterStatus(value)
    }
  }

  const handlePlanChange = (value: string) => {
    if (isValidPlan(value)) {
      setFilterPlan(value)
    }
  }

  return (
    <div className="flex flex-col sm:flex-row gap-3 items-center bg-muted/40 p-1 rounded-lg border">
      <div className="relative flex-1 w-full">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search license keys..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9 border-0 bg-transparent focus-visible:ring-0"
        />
      </div>
      <div className="h-6 w-px bg-border hidden sm:block" />
      <div className="flex items-center gap-2 w-full sm:w-auto px-2">
        <Filter className="h-3.5 w-3.5 text-muted-foreground" />
        <Select value={filterStatus} onValueChange={handleStatusChange}>
          <SelectTrigger className="w-[130px] h-8 border-0 bg-transparent focus:ring-0">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="expired">Expired</SelectItem>
            <SelectItem value="revoked">Revoked</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterPlan} onValueChange={handlePlanChange}>
          <SelectTrigger className="w-[130px] h-8 border-0 bg-transparent focus:ring-0">
            <SelectValue placeholder="Plan" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Plans</SelectItem>
            <SelectItem value="starter">Starter</SelectItem>
            <SelectItem value="professional">Professional</SelectItem>
            <SelectItem value="enterprise">Enterprise</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  )
}
